import React, { type CSSProperties, useCallback } from 'react';
import { ApplicationView, useController, raw } from '@aha-app/mvc';
import TableController from './TableController';
import ColumnResizeHandle from './ColumnResizeHandle';
import {
  ReactEditor,
  useReadOnly,
  useSelected,
  useSlateStatic,
} from 'slate-react';
import { Path, Transforms } from 'slate';
import classnames from 'classnames';
import type {
  TableCellElement,
  ComponentProps,
} from '../../../types/aha-editor';
import styles from './Table.module.css';
import leftButtonPressed from '../leftButtonPressed';
import useAhaEditorFocused from '../../../components/useAhaEditorFocused';
import ColorUtils from '../../../util/color';

interface TableCellComponentProps extends ComponentProps<TableCellElement> {
  children: React.ReactNode;
}

const MIN_COLUMN_WIDTH = 3;

const TableCellComponent: React.FC<TableCellComponentProps> = ({
  attributes,
  element,
  children,
}) => {
  const controller = useController(TableController);
  const { tableElement, isSelecting, tableSelection } = controller.state;
  const editor = useSlateStatic();
  const selected = useSelected();
  const readOnly = useReadOnly();
  const showResizeHandle = !isSelecting && !readOnly;
  const editorFocused = useAhaEditorFocused() && !readOnly;

  const tableStructure = controller.tableStructure;
  const path = ReactEditor.findPath(editor, element);
  const tablePath = path.slice(0, -2);
  const relativePath = path.slice(-2);
  const coords = tableStructure.pathToCoordinates(relativePath);

  const onMouseDown = (event: React.MouseEvent) => {
    if (!leftButtonPressed(event)) return;
    if (!coords) return;

    controller.actionSetIsSelecting(true);
    controller.clickedAnchorCell =
      tableStructure.cell(coords.columnIndex, coords.rowIndex) ?? null;

    if (event.shiftKey) {
      event.stopPropagation();
      controller.actionUpdateSelection(event.nativeEvent);
    }
  };

  const handleMove = useCallback(
    (x: number, rowIndex: number) => {
      // Only run for the handle on the current row.
      // This ensures we don't pointlessly run this function for every row in the table.
      if (rowIndex !== coords?.rowIndex) return;

      const tablePath = ReactEditor.findPath(editor, raw(tableElement));
      const realColumnIndex =
        coords.columnIndex + (element.table_colspan ?? 1) - 1;

      if (realColumnIndex === tableStructure.width - 1) {
        // We are resizing the whole table.
        const domNode = ReactEditor.toDOMNode(editor, raw(tableElement));
        if (!domNode) return;

        const rect = domNode.getBoundingClientRect();
        const width = x - rect.left;

        Transforms.setNodes(editor, { table_width: width }, { at: tablePath });
      } else {
        // We are resizing a column.
        const cellDomNode = ReactEditor.toDOMNode(editor, element);
        if (!cellDomNode) return;

        const tableDomNode = ReactEditor.toDOMNode(editor, raw(tableElement));
        if (!tableDomNode) return;

        const cellRect = cellDomNode.getBoundingClientRect();
        const tableRect = tableDomNode.getBoundingClientRect();

        const newWidth = ((x - cellRect.left) / tableRect.width) * 100;

        if (newWidth < MIN_COLUMN_WIDTH) return;

        let columnWidths: number[] = tableElement.table_columnWidths
          ? [...tableElement.table_columnWidths]
          : [];

        // Make sure the widths are initialized. We start with equal sized columns.
        if (columnWidths.length !== tableStructure.width) {
          columnWidths = Array(tableStructure.width).fill(
            Math.round(100 / tableStructure.width)
          );
        }

        // Memoize the original widths so we don't send an operation
        // if nothing has changed
        const originalWidths = columnWidths.toString();

        const unchangedWidth = columnWidths
          .slice(coords.columnIndex, realColumnIndex)
          .reduce((totalWidth, width) => totalWidth + width, 0);

        const newColumnWidth = Math.round(newWidth - unchangedWidth);
        if (newColumnWidth < MIN_COLUMN_WIDTH) return;

        const siblingPosition = realColumnIndex + 1;
        const sizeDifference = newColumnWidth - columnWidths[realColumnIndex];
        const siblingWidth = columnWidths[siblingPosition];
        const newSiblingWidth = siblingWidth - sizeDifference;
        if (newSiblingWidth < MIN_COLUMN_WIDTH) return;

        columnWidths[realColumnIndex] = newColumnWidth;
        columnWidths[siblingPosition] = newSiblingWidth;

        if (originalWidths !== columnWidths.toString()) {
          Transforms.setNodes(
            editor,
            { table_columnWidths: columnWidths },
            { at: tablePath }
          );
        }
      }
    },
    [coords, editor, element, tableElement, tableStructure.width]
  );

  const isHeader =
    (tableElement.table_headerRow && coords?.rowIndex === 0) ||
    (tableElement.table_headerColumn && coords?.columnIndex === 0);
  const CellTag = isHeader ? 'th' : 'td';

  const style: CSSProperties = {
    backgroundColor: element.table_cellBackgroundColor
      ? ColorUtils.mapColorFromColorPickerToDarkMode(
          element.table_cellBackgroundColor
        )
      : undefined,
    verticalAlign: element.table_cellVerticalAlign,
  };

  const isTableSelected = tableSelection.isTableSelected(tablePath);
  const isCellSelected = tableSelection.isCellSelected(tablePath, relativePath);
  const selectionWithinCell =
    selected &&
    editor.selection &&
    editorFocused &&
    !isCellSelected &&
    Path.isAncestor(path, editor.selection.anchor.path) &&
    Path.isAncestor(path, editor.selection.focus.path);

  return (
    <CellTag
      {...attributes}
      colSpan={element.table_colspan ?? 1}
      rowSpan={element.table_rowspan ?? 1}
      className={classnames(`Editor__${CellTag}`, {
        [styles.cellSelectedBackground]: isTableSelected || isCellSelected,
        [styles.selectionWithinCell]: selectionWithinCell,
      })}
      style={style}
      onMouseDown={onMouseDown}
    >
      {coords && showResizeHandle && (
        <ColumnResizeHandle
          onMove={handleMove}
          rowIndex={coords.rowIndex}
          columnIndex={coords.columnIndex + (element.table_colspan ?? 1) - 1}
        />
      )}
      {children}
    </CellTag>
  );
};

export default ApplicationView(TableCellComponent);
